import WebServiceRequest from '../Api/WebServiceRequest'

// ============================================================
//  MediaLibrary Endpoints (based on OpenAPI spec)
// ============================================================

// 1. Upload a new file
class MediaLibraryUploadRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
        super.setTag('MediaLibrary/upload')
    }
    setParams(data) {
        super.setFormData(data) // expects FormData
    }
}

// 2. Upload and attach to an owner in one request
class MediaLibraryUploadAndAttachRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
        super.setTag('MediaLibrary/upload-and-attach')
    }
    setParams(data) {
        super.setFormData(data) // expects FormData with owner fields
    }
}

// 3. Update metadata
class MediaLibraryUpdateMetadataRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('PUT')
    }
    setParams(data) {
        // data = { fileId, Title, Alt, Description, Tags }
        super.setTag(`MediaLibrary/metadata/${data.fileId}`)
        super.setRequestParamDataObj(data)
    }
}

// 4. Get file details
class MediaLibraryGetFileRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
    }
    setParams(fileId) {
        super.setTag(`MediaLibrary/${fileId}`)
    }
}

// 5. Delete a file
class MediaLibraryDeleteFileRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('DELETE')
    }
    setParams(data) {
        // data = { fileId }
        super.setTag(`MediaLibrary/${data.fileId}`)
        super.setRequestParam(data)
    }
}

// 6. Search files with filters
class MediaLibrarySearchRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
        super.setTag('MediaLibrary/search')
    }
    setParams(data) {
        super.setRequestParam(data) // query parameters
    }
}

// 7. Get files by owner
class MediaLibraryGetByOwnerRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
        super.setTag('MediaLibrary/owner/{ownerType}/{objectId}')
    }
    setParams(data) {
        super.setRequestParam(data)
    }
}

// 8. Attach an existing file to an owner
class MediaLibraryAttachRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
        super.setTag('MediaLibrary/attach')
    }
    setParams(data) {
        super.setRequestParamDataObj(data)
    }
}

// 9. Detach a file from its owner (delete the relationship)
class MediaLibraryDetachRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('DELETE')
        super.setTag('MediaLibrary/detach/{mediaId}')
    }
    setParams(data) {
        // data = { mediaId }
        super.setRequestParam(data)
    }
}

// 10. Replace a file with a new one (same ID)
class MediaLibraryReplaceFileRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('PUT')
    }
    setParams(data) {
        // data = { fileId, NewFile: file }
        super.setTag(`MediaLibrary/replace/${data.fileId}`)
        super.setFormData(data) // expects FormData with NewFile
    }
}

// 11. Get current user's storage usage
class MediaLibraryGetMyUsageRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
        super.setTag('MediaLibrary/my-usage')
    }
}

// 12. Get unattached (orphan) files
class MediaLibraryGetUnattachedRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
        super.setTag('MediaLibrary/unattached')
    }
}

// 13. Admin report with statistics
class MediaLibraryAdminReportRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
        super.setTag('MediaLibrary/admin-report')
    }
}

// 14. Convert a video to HLS with specified qualities
class MediaLibraryConvertToHlsRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
    }
    setParams(data) {
        // data = { fileId, qualities: [...], segmentDuration?, deleteOriginalAfterConversion? }
        super.setTag(`MediaLibrary/convert-to-hls/${data.fileId}`)
        super.setRequestParamDataObj(data)
    }
}

// 15. Toggle encryption for a file
class MediaLibraryToggleEncryptionRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
    }
    setParams(data) {
        // data = { fileId, enable: boolean }
        super.setTag(`MediaLibrary/encryption/${data.fileId}`)
        super.setRequestParamDataObj(data)
    }
}

// 16. Get playback URL and token for a file (for secure HLS)
class MediaLibraryGetPlaybackRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
    }
    setParams(fileId) {
        super.setTag(`MediaLibrary/playback/${fileId}`)
    }
}

// 17. Download a file (direct download URL)
// Usually just used to generate a link, but we can keep a class for consistency
class MediaLibraryDownloadFileRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
    }
    setParams(fileId) {
        super.setTag(`MediaLibrary/download/${fileId}`)
    }
}

// 18. Toggle public visibility of a file
class MediaLibraryTogglePublicRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
    }
    setParams(data) {
        super.setTag(`MediaLibrary/public/${data.fileId}`)
        const isPublic = data.isPublic
        super.setRequestParamDataObj(`${isPublic}`)
    }
}

// 19. Get detailed usage breakdown by variant type
class MediaLibraryGetUsageDetailsRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
        super.setTag('MediaLibrary/usage-details')
    }
}

// ============================================================
//  Exports
// ============================================================

export {
    MediaLibraryUploadRequest,
    MediaLibraryUploadAndAttachRequest,
    MediaLibraryUpdateMetadataRequest,
    MediaLibraryGetFileRequest,
    MediaLibraryDeleteFileRequest,
    MediaLibrarySearchRequest,
    MediaLibraryGetByOwnerRequest,
    MediaLibraryAttachRequest,
    MediaLibraryDetachRequest,
    MediaLibraryReplaceFileRequest,
    MediaLibraryGetMyUsageRequest,
    MediaLibraryGetUnattachedRequest,
    MediaLibraryAdminReportRequest,
    MediaLibraryConvertToHlsRequest,
    MediaLibraryToggleEncryptionRequest,
    MediaLibraryGetPlaybackRequest,
    MediaLibraryDownloadFileRequest,
    MediaLibraryTogglePublicRequest,
    MediaLibraryGetUsageDetailsRequest
}
